import type { PageItemContextGateProps } from "./page-item-context-gate.type";

import { Navigate, useLocation } from "react-router";

import { getModelRoutes } from "@/modules/model";
import {
  convertContextRoute,
  useOrganizationContext,
} from "@/modules/organization/organization-context";
import { PageError } from "@/modules/page-error";
import { usePageModalLocationState } from "@/modules/page-modal";
import { isError404 } from "@/services/api";
import { useIdParam } from "@/services/router";
import { LoaderFullscreen } from "@/ui/loader-fullscreen";

import { PageItemContextGateNotifyHandler } from "./page-item-context-gate-notify-handler";
import { getModalRedirectState } from "./page-item-context-gate.helper";

export const PageItemContextGate = ({
  model,
  itemContext,
  queryError,
  isModalPage,
  modalPageLoading,
  children,
}: PageItemContextGateProps) => {
  const { contextSlug, isLoading } = useOrganizationContext();
  const idParam = useIdParam();
  const { pathname, search, state } = useLocation();
  const { showLoaderInModal, ...modalState } = usePageModalLocationState();
  const route = getModelRoutes(model);

  if (isLoading) return;

  if (modalPageLoading && !showLoaderInModal) return <LoaderFullscreen />;

  if (!idParam || isError404(queryError)) {
    const redirectTo = convertContextRoute({
      pathname,
      route: route.list,
      context: contextSlug,
    });

    return <Navigate to={redirectTo + search} replace />;
  }

  if (itemContext && itemContext.slug !== contextSlug) {
    const redirectState = isModalPage
      ? getModalRedirectState({ state, modalState, itemContext })
      : undefined;

    const redirectTo = convertContextRoute({
      pathname,
      route: route.item,
      context: itemContext.slug,
    });

    return (
      <>
        <PageItemContextGateNotifyHandler itemContext={itemContext.slug} />
        <Navigate to={redirectTo + search} state={redirectState} replace />
      </>
    );
  }

  if (queryError) {
    return <PageError status={500} />;
  }

  return children;
};
